import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { Truck, Plus, Trash2, Mail, Phone, MapPin, Package } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { getNav } from "@/pages/navConfigs";

const CATEGORIES = ["catering", "printing", "trophies", "av", "transport", "stationery", "photography", "decor", "security", "medical", "other"];

export default function VendorDirectoryPage() {
  const { user } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [filter, setFilter] = useState("");
  const [form, setForm] = useState({ name: "", category: "catering", contact: "", email: "", phone: "", city: "", notes: "" });

  const load = async () => {
    const r = await api.get("/vendors");
    setVendors(r.data);
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Vendor name is required");
    try {
      await api.post("/vendors", form);
      toast.success("Vendor added");
      setForm({ name: "", category: "catering", contact: "", email: "", phone: "", city: "", notes: "" });
      load();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Failed");
    }
  };

  const del = async (v) => {
    if (!window.confirm(`Remove vendor "${v.name}" from directory?`)) return;
    try {
      await api.delete(`/vendors/${v.vendor_id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const canManage = ["admin", "regional_pm"].includes(user.role);
  const filtered = vendors.filter((v) =>
    !filter || [v.name, v.category, v.city, v.email, v.phone].some((f) => (f || "").toLowerCase().includes(filter.toLowerCase()))
  );

  return (
    <Layout navItems={getNav(user.role)}>
      <div className="p-6 lg:p-10 max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-2xl brand-gradient flex items-center justify-center text-white"><Truck className="w-6 h-6" /></div>
          <div>
            <h1 className="font-display font-black text-2xl text-slate-900">Vendor Directory</h1>
            <p className="text-sm text-slate-500">{canManage ? "Manage shared vendors — visible to every Project Manager across events." : "Read-only view of vendors added by Admin / Regional PM."}</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {canManage && (
            <form onSubmit={add} className="dashboard-card p-5 space-y-2 h-fit" data-testid="vendor-directory-form">
              <h4 className="font-bold flex items-center gap-2"><Plus className="w-4 h-4 text-orange-600" /> Add Vendor</h4>
              <input required placeholder="Vendor name" className="input-pill" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="vendor-name-input" />
              <select className="input-pill" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="vendor-category-input">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c[0].toUpperCase() + c.slice(1)}</option>)}
              </select>
              <input placeholder="Contact person" className="input-pill" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} />
              <input type="email" placeholder="Email" className="input-pill" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="vendor-email-input" />
              <input placeholder="Phone" className="input-pill" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="vendor-phone-input" />
              <input placeholder="City" className="input-pill" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
              <textarea rows={2} placeholder="Notes (payment terms, capacity, prior work...)" className="input-pill" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              <button className="btn-primary w-full py-2.5" type="submit" data-testid="vendor-add-btn"><Plus className="w-4 h-4" /> Add to directory</button>
            </form>
          )}

          <div className={`${canManage ? "lg:col-span-2" : "lg:col-span-3"} dashboard-card p-5`}>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-bold">Directory ({vendors.length})</h4>
              <input placeholder="Search name, category, city…" className="input-pill py-1.5 text-sm w-56" value={filter} onChange={(e) => setFilter(e.target.value)} />
            </div>
            {filtered.length === 0 && <div className="p-8 text-center text-slate-400 text-sm">No vendors match — {canManage ? "add one from the left." : "ask Admin/Regional PM to add vendors."}</div>}
            <div className="grid sm:grid-cols-2 gap-3">
              {filtered.map((v) => (
                <div key={v.vendor_id} className="p-4 rounded-xl border border-slate-100 hover:border-orange-200" data-testid={`vendor-card-${v.vendor_id}`}>
                  <div className="flex items-start justify-between">
                    <div className="min-w-0">
                      <div className="font-bold text-slate-900">{v.name}</div>
                      <div className="text-xs px-2 py-0.5 inline-block rounded-full bg-orange-50 text-orange-700 mt-1">{v.category}</div>
                    </div>
                    {canManage && (
                      <button onClick={() => del(v)} className="text-rose-500 hover:bg-rose-50 p-1 rounded" data-testid={`vendor-del-${v.vendor_id}`}><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                  <div className="mt-2 space-y-1 text-xs text-slate-500">
                    {v.email && <div className="flex items-center gap-1"><Mail className="w-3 h-3" /> {v.email}</div>}
                    {v.phone && <div className="flex items-center gap-1"><Phone className="w-3 h-3" /> {v.phone}</div>}
                    {v.city && <div className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {v.city}</div>}
                    {v.contact && <div className="flex items-center gap-1"><Package className="w-3 h-3" /> Contact: {v.contact}</div>}
                    {v.notes && <div className="pt-1 text-slate-400 italic">{v.notes}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
